import { Logger, LogSender } from '../../../logger';
import StreamingError, {
  StreamingErrorCodes,
} from '../../abstract/StreamingError';
import { StreamingEventBus } from '../../abstract/StreamingEventBus';
import {
  DoryMessageType,
  DoryServiceType,
} from '../../abstract/StreamingMessages';
import StreamingSubscription, {
  StreamingSubscriptionEventHandler,
} from '../../abstract/StreamingSubscription';
import {
  getQuoteSubscribeRequest,
  QuoteRequestPayload,
  QuoteSnapshotResponse,
  QuoteSnapshotResponseRaw,
  QuoteSubscribeRequest,
  QuoteSubscribeResponse,
  QuoteUpdateResponse,
  QuoteUpdateResponseRaw,
} from './Messages';

export type QuoteOnSubscribe = (
  msg: QuoteSubscribeResponse,
  err: StreamingError
) => void;
export type QuoteOnSnapshot = (
  msg: QuoteSnapshotResponse,
  err: StreamingError
) => void;
export type QuoteOnUpdate = (
  msg: QuoteUpdateResponse,
  err: StreamingError
) => void;

export default class QuoteSubscription extends StreamingSubscription<
  QuoteRequestPayload,
  QuoteSubscribeRequest
> {
  // All subscribed rics
  private _rics: string[] = [];
  // All subscribed fids
  private _fids: string[] = [];

  public get subscribed(): QuoteRequestPayload {
    return {
      rics: this._rics,
      fids: this._fids,
    };
  }
  public get SubscribeRequest(): QuoteSubscribeRequest {
    return getQuoteSubscribeRequest(this.id, [this.subscribed]);
  }

  public readonly on: {
    [DoryMessageType.SUBSCRIBE]: StreamingSubscriptionEventHandler<QuoteSubscribeResponse>;
    [DoryMessageType.SNAPSHOT]: StreamingSubscriptionEventHandler<QuoteSnapshotResponse>;
    [DoryMessageType.UPDATE]: StreamingSubscriptionEventHandler<QuoteUpdateResponse>;
  } = {
    /**
     * The handler for "subscribe" has to be called when:
     * - a message of with messageType "subscribe" is received and messageId equal to this.id
     *
     * The handler is stopped when:
     * - it's called
     *
     * Attach an error object if:
     * - payload.subscription.failed !== 0
     */
    [DoryMessageType.SUBSCRIBE]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.SUBSCRIBE].isAttached = true;
        this.on[DoryMessageType.SUBSCRIBE].detach =
          this.eventBus.once<QuoteSubscribeResponse>(
            DoryServiceType.QUOTE,
            DoryMessageType.SUBSCRIBE,
            (data) => {
              let err: StreamingError = undefined;
              if (data.payload.subscription.failed.length > 0) {
                const failed = data.payload.subscription.failed;
                err = new StreamingError(
                  'Streaming.Quote.QuoteSubscription.onSubscribe',
                  StreamingErrorCodes.INVALID_RIC,
                  `Failed to subscribe to ${failed.length} out of ${this._rics.length} rics. Failed: "${failed}".`
                );
              }

              this.on[DoryMessageType.SUBSCRIBE].handler(data, err);
              this.on[DoryMessageType.SUBSCRIBE].isAttached = false;
              this.stopTimeout();
            },
            (data) => data.header.messageId === this.id
          );
      },
    },
    /**
     * The handler for "snapshot" has to be called when:
     * - a message of with messageType "snapshot" is received that's not empty and either:
     *   - messageId equal to this.id is received or
     *   - it contains at least all subscribed fids and one of the subscribed rics
     *
     * The handler is stopped when:
     * - snapshots for all requested rics have arrived (this.pending.length === 0)
     *
     * Attach an error object if:
     * - never
     */
    [DoryMessageType.SNAPSHOT]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.SNAPSHOT].isAttached = true;
        this.on[DoryMessageType.SNAPSHOT].detach =
          this.eventBus.on<QuoteSnapshotResponseRaw>(
            DoryServiceType.QUOTE,
            DoryMessageType.SNAPSHOT,
            (data) => {
              Logger.silly(LogSender.STREAMING_QUOTE, 'Snapshot received');
              // Sometimes the Backend sends empty snapshots and re-sends the complete snapshot later
              // so we ignore empty snapshots
              if (
                data.payload[0].values.filter((i: string) => i !== '')
                  .length === 0
              )
                return;
              Logger.silly(LogSender.STREAMING_QUOTE, 'Snapshot not empty');
              // This is not the snapshot you are looking for
              if (
                // If the header has a messageId, check if it equals the id of the subscription
                data.header.messageId === this.id ||
                // Otherwise check if the response contains all requested fids
                this.checkSnapshotBody(data)
              ) {
                Logger.silly(
                  LogSender.STREAMING_QUOTE,
                  'Snapshot matches subscription'
                );
                const missingFids = this._fids.filter(
                  (e) => data.payload[0].fids.indexOf(e) === -1
                );
                if (missingFids.length > 0) {
                  Logger.warn(
                    LogSender.STREAMING_QUOTE,
                    `Detected missing Fids (${missingFids}) in Snapshot for Ric "${data.payload[0].ric} (Quote Subscription ${this.id})"`
                  );
                }

                // Parse raw response
                const msg = this.parseResponse(data);
                const index = this.pending.indexOf(msg.payload[0].ric);

                if (index !== -1) {
                  this.on[DoryMessageType.SNAPSHOT].handler(
                    this.parseResponse(data),
                    undefined
                  );
                  this.pending.splice(index, 1);
                  Logger.silly(
                    LogSender.STREAMING_QUOTE,
                    'Snapshot: removed ric from pending'
                  );
                }
                if (!this.isPending) {
                  this.on[DoryMessageType.SNAPSHOT]?.detach();
                  this.on[DoryMessageType.SNAPSHOT].detach = undefined;
                  this.on[DoryMessageType.SNAPSHOT].isAttached = false;
                  Logger.silly(
                    LogSender.STREAMING_QUOTE,
                    'Snapshot handler stopped'
                  );
                }
              }
            }
          );
      },
    },
    /**
     * The handler for "update" has to be called when:
     * - a message of with messageType "update" is received that contains one of the subscribed rics
     *
     * The handler is stopped when:
     * - the subscription is stopped
     *
     * Attach an error object if:
     * - never
     */
    [DoryMessageType.UPDATE]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.UPDATE].isAttached = true;
        this.on[DoryMessageType.UPDATE].detach =
          this.eventBus.on<QuoteUpdateResponseRaw>(
            DoryServiceType.QUOTE,
            DoryMessageType.UPDATE,
            (data) => {
              // Check if message ric matches ric, payload[0] is hardcoded because the protocol defines this
              // field as array with a constant length = 1
              if (this._rics.indexOf(data.payload[0].ric) !== -1) {
                this.on[DoryMessageType.UPDATE].handler(
                  this.parseResponse(data),
                  undefined
                );
              }
            }
          );
      },
    },
  };

  constructor(
    id: string,
    eventBus: StreamingEventBus,
    rics: string[],
    fids: string[],
    onSubscribe: QuoteOnSubscribe,
    onSnapshot: QuoteOnSnapshot,
    onUpdate: QuoteOnUpdate
  ) {
    super(id, eventBus);

    this._rics = rics;
    this._fids = fids;

    this.on[DoryMessageType.SUBSCRIBE].handler = onSubscribe;
    this.on[DoryMessageType.SNAPSHOT].handler = onSnapshot;
    this.on[DoryMessageType.UPDATE].handler = onUpdate;
  }

  public start(timeout: number): void {
    this.pending = [...this._rics];
    // Attach all event listeners
    this.on[DoryMessageType.SUBSCRIBE].attach();
    this.on[DoryMessageType.SNAPSHOT].attach();
    this.on[DoryMessageType.UPDATE].attach();
    // Start pending timeout
    this.startTimeout(
      timeout,
      'Streaming.Quote.QuoteSubscription.start',
      this.on[DoryMessageType.SUBSCRIBE].handler
    );
  }
  public restart(timeout: number, pending: string[] = this._rics): void {
    this.pending = [...pending];
    // Attach all event listeners that might be detached
    if (!this.on[DoryMessageType.SUBSCRIBE].isAttached) {
      this.on[DoryMessageType.SUBSCRIBE].attach();
    }
    if (!this.on[DoryMessageType.SNAPSHOT].isAttached) {
      this.on[DoryMessageType.SNAPSHOT].attach();
    }
    // Start pending timeout
    this.startTimeout(
      timeout,
      'Streaming.Quote.QuoteSubscription.restart',
      this.on[DoryMessageType.SUBSCRIBE].handler
    );
  }
  public stop(): void {
    // Stop all event listeners
    if (this.on[DoryMessageType.SUBSCRIBE].detach) {
      this.on[DoryMessageType.SUBSCRIBE].detach();
      Logger.silly(LogSender.STREAMING_QUOTE, 'Detached Subscribe listener');
    }
    if (this.on[DoryMessageType.SNAPSHOT].detach) {
      this.on[DoryMessageType.SNAPSHOT].detach();
      Logger.silly(LogSender.STREAMING_QUOTE, 'Detached Snapshot listener');
    }
    if (this.on[DoryMessageType.UPDATE].detach) {
      this.on[DoryMessageType.UPDATE].detach();
      Logger.silly(LogSender.STREAMING_QUOTE, 'Detached Update listener');
    }
    // Stop pending timeout
    this.stopTimeout();
  }

  //#region Modify Subscription
  public addRics(rics: string[]): void {
    this._rics = this._rics.concat(
      rics.filter((ric) => !this._rics.includes(ric))
    );
  }
  public removeRics(rics: string[]): void {
    for (const ric of rics) {
      const index = this._rics.findIndex((e) => e === ric);
      if (index !== -1) {
        this._rics.splice(index, 1);
      }
    }
  }
  public addFids(fids: string[]): void {
    this._fids = this._fids.concat(
      fids.filter((fid) => !this._fids.includes(fid))
    );
  }
  public removeFids(fids: string[]): void {
    for (const fid of fids) {
      const index = this._fids.findIndex((e) => e === fid);
      if (index !== -1) {
        this._fids.splice(index, 1);
      }
    }
  }
  //#endregion

  /**
   * Parse the message sent from the WebSocket into a an Object
   *
   * @param response Raw server response
   * @returns Parsed response
   */
  private parseResponse(
    response: QuoteSnapshotResponseRaw | QuoteUpdateResponseRaw
  ): QuoteSnapshotResponse | QuoteUpdateResponse {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload = response.payload.map((item: any) => {
      const data: {
        [key: string]: string;
      } = {};
      for (let i = 0; i < item.fids.length; i++) {
        data[item.fids[i]] = item.values[i];
      }

      return {
        ric: item.ric,
        data,
      };
    });

    return {
      header: response.header,
      payload,
    };
  }

  /**
   * Checks if the response matches the requested rics and fids
   *
   * @param snapshot
   * @returns If snapshot contains at least all requested Fids and if for one of the requested Rics
   */
  private checkSnapshotBody(snapshot: QuoteSnapshotResponseRaw) {
    // payload[0] is hardcoded because the protocol defines this field as array with a constant length = 1
    return (
      // Check if message ric is part of subscribed rics
      this._rics.indexOf(snapshot.payload[0].ric) !== -1 &&
      // Check if message contains all required fids
      this._fids.every((i: string) => snapshot.payload[0].fids.includes(i))
    );
  }
}
